import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { m5m3Api, ReverseCandidate } from '../../services/m5m3Api';
import { BatchAcceptanceBridge, pwm5m3f1AcceptanceManifest } from '../../features/acceptance';
import './M5M3Form.css';
import { useModulePermission } from '../../hooks/usePermission';

// ---- Item state with selection ----
interface ItemState {
  item: ReverseCandidate;
  selected: boolean;
}

// ---- Component ----
const M5M3Form: React.FC = () => {
  const navigate = useNavigate();
  const { canView, canApprove } = useModulePermission('pwm5m3f1');
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await m5m3Api.queryCandidates(startNo, endNo);
      setLastApiResponse(res);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的交運清單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fm130_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute reversal ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要沖銷的交運清單');
      return;
    }
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆交運清單？此操作不可復原。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items
          .filter((s) => s.selected)
          .map((s) => s.item.fm130_01);
        setExecuting(true);
        try {
          const res = await m5m3Api.reverse(selectedNos);
          setLastApiResponse(res);
          if (res.failed === 0) {
            message.success(res.message || `沖銷完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '沖銷執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fm130_01, e.target.checked)}
        />
      ),
    },
    {
      title: '交運清單號',
      dataIndex: ['item', 'fm130_01'],
      key: 'fm130_01',
      width: 120,
    },
    {
      title: '交運日期',
      dataIndex: ['item', 'fm130_11'],
      key: 'fm130_11',
      width: 120,
    },
    {
      title: '客戶代碼',
      dataIndex: ['item', 'fm130_16'],
      key: 'fm130_16',
      width: 120,
    },
    {
      title: '部門代碼',
      dataIndex: ['item', 'fm130_20'],
      key: 'fm130_20',
      width: 100,
    },
    {
      title: '負責人',
      dataIndex: ['item', 'fm130_22'],
      key: 'fm130_22',
      width: 100,
    },
    {
      title: '狀態',
      dataIndex: ['item', 'fm130_80'],
      key: 'fm130_80',
      width: 70,
    },
  ];
  const candidateRecords = items.map((state) => state.item);
  const selectedKeys = items.filter((state) => state.selected).map((state) => state.item.fm130_01);
  const queryAcceptanceExact = useCallback(
    (id: string) => m5m3Api.queryCandidates(id, id),
    [],
  );

  // ---- Render ----
  return (
    <div className="m5m3-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                庫存管理
              </a>
              <span>/</span>
              <span className="current">庫存交運清單核准沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>庫存交運清單核准沖銷</h1>
            <span className="badge">M5M3</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label data-contract-field="fm130_01">交運清單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button data-acceptance-action="PWM5M3-ACT-QUERY" type="primary" onClick={onQuery} loading={loading} disabled={!canView}>
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fm130_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入交運清單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            data-acceptance-action="PWM5M3-ACT-REVERSE"
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing || !canApprove}
            loading={executing}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwm5m3f1AcceptanceManifest}
          records={candidateRecords}
          selectedKeys={selectedKeys}
          keyOf={(item) => item.fm130_01}
          queryExact={queryAcceptanceExact}
          lastApiResponse={lastApiResponse}
          loading={loading || executing}
          onAutoFill={() => {
            if (candidateRecords[0]) handleToggle(candidateRecords[0].fm130_01, true);
          }}
          autoFillDisabled={candidateRecords.length === 0}
          autoFillReason="先查詢可撤回的已核准交運單，再安全勾選第一筆；不會自動撤回。"
          screenDraft={{ startNo, endNo, selected_items: selectedKeys, candidates: candidateRecords }}
        />
      </div>
    </div>
  );
};

export default M5M3Form;
